<template>
  <div
    class="flex w-full items-center gap-3"
    role="search"
  >
    <!-- The venue search field from Places, at 32px to sit inside the strip; the after: box restores a 44px target. -->
    <label
      class="pj-field relative flex h-8 min-w-0 flex-1 items-center gap-2 rounded-full bg-surface px-3.5 after:absolute after:inset-x-0 after:-inset-y-1.5 lg:max-w-measure"
    >
      <PjIcon
        class="shrink-0 text-muted"
        :name="PJ_ICON.SEARCH"
        size="meta"
      />
      <span class="sr-only">Search the menu</span>
      <input
        ref="inputEl"
        v-model="query"
        autocomplete="off"
        class="min-w-0 flex-1 bg-transparent text-body text-ink placeholder:text-muted"
        enterkeyhint="search"
        placeholder="Search dishes"
        type="search"
        @keydown.esc="emit('close')"
      />
    </label>
    <button
      class="h-tap shrink-0 text-body text-ink pj-tap"
      type="button"
      @click="emit('close')"
    >
      Cancel
    </button>

    <p
      aria-live="polite"
      class="sr-only"
    >
      {{ announcement }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { refDebounced } from '@vueuse/core'
import { computed, onMounted, ref, toRef } from 'vue'

import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'

/**
 * The menu search field, laid over the category tabs while it is open.
 * Matching lives in `searchMenu`; this owns the field, Cancel and the spoken count.
 */
const props = defineProps<{
  /** Dishes the current query finds. */
  resultCount: number
}>()

const emit = defineEmits<{ close: [] }>()

const query = defineModel<string>('query', { required: true })

/** How long typing must pause before the count is read out, so a screen reader is not interrupted on every key. */
const ANNOUNCE_PAUSE_MS = 600

const inputEl = ref<HTMLInputElement | null>(null)

const settledCount = refDebounced(toRef(props, 'resultCount'), ANNOUNCE_PAUSE_MS)

const settledQuery = refDebounced(query, ANNOUNCE_PAUSE_MS)

const announcement = computed(() => {
  if (settledQuery.value.trim() === '') return ''
  if (settledCount.value === 0) return 'No dishes found'

  return settledCount.value === 1 ? '1 dish found' : `${settledCount.value} dishes found`
})

onMounted(() => inputEl.value?.focus())
</script>
